// src/oura/merchant/AccessDrawer.tsx
//
// One access item, opened from the merchant's Set-up screen: why we need it,
// the steps, how to remove us, and the one action that moves it on. After a
// forward action the footer offers the next item, so the merchant can walk
// through all 4 without going back to the list.
//
// Everything that answers a press lives in the footer, because the footer is
// the one part of a drawer that is always on screen.
//
// The parent gives this a `key` per item, so each item opens at the top.

import { useEffect, useRef, useState, type ReactNode } from "react";
import { Loader2Icon } from "lucide-react";
import { cn } from "../lib/cn";
import {
    Dot, Drawer, DrawerBlock, FOCUS_RING, PillButton, Seg, Tag, TYPE, type Tone } from "@oura/ui";
import {
    claimMyAccess,
    connectMyAccess,
    disconnectMyAccess,
    type AccessItem,
    type AccessKey,
    type AccessStatus,
    type MerchantConnectResult,
    type MerchantOnboarding,
    type SiteBuilder,
} from "@service/aeo/onboarding";
import { dayMonth, isHeld, merchantProgress } from "@oura/onboarding/derive";
import { useViewAs } from "@hook/use-view-as";
import {
    ACCESS_LOSS, ACCESS_REMOVE, ACCESS_WHY, GBP_STEPS, GOOGLE_STEPS, MERCHANT_ACCESS_NAME, SITE_BUILDER_ORDER, SITE_GUIDES,
} from "@oura/onboarding/guides";
import { AccessAddress, GuideSteps } from "@oura/onboarding/StepText";

const STATUS_TAG: Record<AccessStatus, { tone: Tone; label: string }> = {
    not_started: { tone: "gray", label: "Not started" },
    // We are checking what they gave: ours now, so the brand's "In progress" (audit, 24 Oct 2026).
    claimed: { tone: "blue", label: "In progress" },
    confirmed: { tone: "green", label: "Done" },
    connected: { tone: "green", label: "Done" },
};

/** The item's status, once. A status the screen does not know renders nothing. */
export function AccessStatusTag({ item }: { item: AccessItem }) {
    const style = STATUS_TAG[item.status] as { tone: Tone; label: string } | undefined;
    if (!style) return null;
    return <Tag tone={style.tone}>{style.label}</Tag>;
}

/**
 * The same status as a DOT AND A WORD, for the redesigned Setup rows (#4062
 * MR-FE-9).
 *
 * A pill where a button belongs reads as pressable and is not; the right of a
 * row is for something that does something. The status moves under the name
 * with the reason line, and keeps the words it has always had.
 */
export function AccessStatusLine({ item }: { item: AccessItem }) {
    const style = STATUS_TAG[item.status] as { tone: Tone; label: string } | undefined;
    if (!style) return null;
    return (
        <span className="inline-flex items-center gap-1.5">
            <Dot tone={style.tone} />
            {style.label}
        </span>
    );
}

/** A labelled text input in the kit's look, with its error under it. */
export function AccessField({
    id,
    label,
    value,
    onChange,
    error,
    type = "text",
    autoComplete,
    placeholder,
}: {
    id: string;
    label: string;
    value: string;
    onChange: (value: string) => void;
    error?: string;
    type?: "text" | "email";
    autoComplete?: string;
    placeholder?: string;
}) {
    const errorId = `${id}-error`;
    return (
        <div>
            <label htmlFor={id} className={cn(TYPE.lab, "mb-1 block")}>
                {label}
            </label>
            <input
                id={id}
                type={type}
                value={value}
                autoComplete={autoComplete}
                placeholder={placeholder}
                spellCheck={false}
                aria-invalid={error ? true : undefined}
                aria-describedby={error ? errorId : undefined}
                onChange={(e) => onChange(e.target.value)}
                className={cn(
                    // 16px on a phone: smaller text makes iPhones zoom in on a tap.
                    "h-10 w-full rounded-xl bg-white px-3 text-base text-gray-900 ring-1 ring-inset placeholder:text-gray-400 sm:text-sm",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600",
                    error ? "ring-rose-600" : "ring-black/[0.08]",
                )}
            />
            {error && (
                <p id={errorId} role="alert" className={cn(TYPE.tc, "m-0 mt-1 font-medium text-rose-600")}>
                    {error}
                </p>
            )}
        </div>
    );
}

const NOT_SAVED = "That did not go through. Try again in a moment.";
/** Taller pills on a phone, where they are tapped. */
const TAP = "max-sm:h-9";
/** Marks the footer control that takes focus once an action has replaced the one that was pressed. */
const LAND = "[data-access-land]";

export interface AccessDrawerProps {
    item: AccessItem;
    onboarding: MerchantOnboarding;
    /** The next item the merchant has not started, or null when none is left. */
    nextKey: AccessKey | null;
    onClose: () => void;
    onChanged: (updated: MerchantOnboarding) => void;
    onNext: () => void;
}

export function AccessDrawer({ item, onboarding, nextKey, onClose, onChanged, onNext }: AccessDrawerProps) {
    const key = item.key;
    const held = isHeld(item);
    const byInvite = key === "gbp" || key === "website";

    const [builder, setBuilder] = useState<SiteBuilder>(onboarding.siteBuilder ?? "other");
    const guide = SITE_GUIDES[builder];
    const [failure, setFailure] = useState<string | null>(null);
    const [busy, setBusy] = useState<"primary" | "undo" | "remove" | null>(null);
    /** Set after a forward action: the footer then offers the next item. */
    const [advanced, setAdvanced] = useState(false);
    /** What the last press did, in 1 word, for the footer to say. */
    const [said, setSaid] = useState<string | null>(null);
    /** Disconnect asks once more, and keeps asking until it is answered. */
    const [removing, setRemoving] = useState(false);
    /** Counts the presses that changed the footer, so focus follows each one. */
    const [moved, setMoved] = useState(0);
    const foot = useRef<HTMLDivElement>(null);
    const { active: readOnly } = useViewAs();

    // The pressed button is gone once the footer changes. Focus goes to what
    // replaced it, so a keyboard is never dropped back on the page.
    useEffect(() => {
        if (moved > 0) foot.current?.querySelector<HTMLElement>(LAND)?.focus();
    }, [moved]);

    const run = async (which: "primary" | "undo" | "remove", action: () => Promise<MerchantOnboarding>, after: { advance: boolean; say: string | null }) => {
        setBusy(which);
        setFailure(null);
        try {
            onChanged(await action());
            setAdvanced(after.advance);
            setSaid(after.say);
            setRemoving(false);
        } catch {
            setFailure(NOT_SAVED);
        } finally {
            setBusy(null);
            setMoved((n) => n + 1);
        }
    };

    const connect = async () => {
        setBusy("primary");
        setFailure(null);
        let result: MerchantConnectResult;
        try {
            result = await connectMyAccess(key, {});
        } catch {
            result = { ok: false, message: NOT_SAVED };
        }
        if (!result.ok) {
            setFailure(result.message);
            setBusy(null);
            setMoved((n) => n + 1);
            return;
        }
        if (result.kind === "redirect") {
            // Google's own screen takes over; the button stays busy until the page leaves.
            window.location.assign(result.url);
            return;
        }
        onChanged(result.onboarding);
        setAdvanced(true);
        setSaid("Connected.");
        setBusy(null);
        setMoved((n) => n + 1);
    };

    // An invite is sent. A user is added. The footer says the one that happened.
    const claimedWord = key === "website" && guide.kind === "user" ? "Done." : "Sent.";
    const claim = () => void run("primary", () => claimMyAccess(key, true, key === "website" ? builder : undefined), { advance: true, say: claimedWord });
    const undo = () => void run("undo", () => claimMyAccess(key, false), { advance: false, say: "Undone." });
    const remove = () => void run("remove", () => disconnectMyAccess(key), { advance: false, say: "Disconnected." });
    const askRemove = () => { setRemoving(true); setMoved((n) => n + 1); };
    const keep = () => { setRemoving(false); setMoved((n) => n + 1); };

    const note = (text: ReactNode) => <span role="status" className={cn(TYPE.tc, "mr-auto text-gray-500")}>{text}</span>;

    let actions: ReactNode = null;
    if (removing) {
        actions = (
            <>
                <p className={cn(TYPE.tc, "m-0 basis-full text-gray-700")}>{ACCESS_LOSS[key]} You can connect again any time.</p>
                <PillButton data-access-land="" className={TAP} disabled={busy === "remove"} onClick={keep}>
                    Keep it
                </PillButton>
                {/* Its own button: a pill's colours cannot be swapped from outside. */}
                <button
                    type="button"
                    disabled={busy === "remove"}
                    onClick={remove}
                    className={cn(
                        TYPE.tc,
                        "inline-flex h-8 items-center justify-center gap-1.5 whitespace-nowrap rounded-full bg-rose-600 px-4 font-medium text-white transition-colors hover:bg-rose-700 disabled:opacity-40",
                        TAP,
                        FOCUS_RING,
                    )}
                >
                    {busy === "remove" && <Loader2Icon aria-hidden size={14} className="animate-spin" />}
                    Disconnect
                </button>
            </>
        );
    } else if (advanced) {
        const { mine } = merchantProgress(onboarding);
        actions = (
            <>
                {note(<>{said} {mine > 0 ? `${mine} left for you.` : "Your part is done."}</>)}
                {item.status === "claimed" ? (
                    <PillButton variant="text" className={TAP} busy={busy === "undo"} onClick={undo}>Undo</PillButton>
                ) : (
                    <PillButton variant="text" className={TAP} onClick={askRemove}>Disconnect</PillButton>
                )}
                {nextKey ? (
                    <PillButton data-access-land="" variant="primary" className={TAP} onClick={onNext}>
                        Next: {MERCHANT_ACCESS_NAME[nextKey]}
                    </PillButton>
                ) : (
                    <PillButton data-access-land="" variant="primary" className={TAP} onClick={onClose}>
                        Finish
                    </PillButton>
                )}
            </>
        );
    } else if (item.status === "connected") {
        actions = (
            <>
                {note(item.since ? `Since ${dayMonth(item.since)}` : null)}
                <PillButton data-access-land="" className={TAP} onClick={askRemove}>Disconnect</PillButton>
            </>
        );
    } else if (item.status === "confirmed") {
        actions = item.since ? note(`Since ${dayMonth(item.since)}`) : null;
    } else if (item.status === "claimed") {
        actions = (
            <>
                {note(item.claimedAt ? `You told us ${dayMonth(item.claimedAt)}` : null)}
                <PillButton data-access-land="" className={TAP} busy={busy === "undo"} onClick={undo}>Undo</PillButton>
            </>
        );
    } else if (item.status === "not_started") {
        actions = (
            <>
                {note(said)}
                <PillButton data-access-land="" variant="primary" className={TAP} busy={busy === "primary"} onClick={byInvite ? claim : () => void connect()}>
                    {key === "gbp" ? "I've sent the invite" : key === "website" ? guide.done : "Connect Google"}
                </PillButton>
            </>
        );
    }

    const told = onboarding.siteBuilder ? SITE_GUIDES[onboarding.siteBuilder] : null;

    return (
        <Drawer
            open
            // Escape answers the question on screen first: while Disconnect is asking, it means Keep it.
            onClose={removing ? keep : onClose}
            width={560}
            title={MERCHANT_ACCESS_NAME[key]}
            sub={<AccessStatusTag item={item} />}
            // The row that opened this item, found again after the list re-renders.
            returnFocus={() => document.querySelector<HTMLElement>(`[data-access-row="${key}"]`)}
            foot={
                (actions || failure) && (
                    <div ref={foot} className="flex w-full flex-wrap items-center gap-2">
                        {failure && <p role="alert" className={cn(TYPE.tc, "m-0 basis-full rounded-xl bg-rose-50 px-3 py-2 font-medium text-rose-700")}>{failure}</p>}
                        {/* Every action here writes (an invite sent, a connection made or taken back). Staff viewing as the merchant read; the fieldset disables the lot (#4112). */}
                        <fieldset disabled={readOnly} className="contents">{actions}</fieldset>
                    </div>
                )
            }
        >
            <DrawerBlock label="Why">{ACCESS_WHY[key]}</DrawerBlock>

            {item.status === "not_started" && (
                <DrawerBlock label="Steps">
                    {key === "gbp" && (
                        <>
                            <GuideSteps steps={GBP_STEPS} />
                            <AccessAddress className="mt-3" />
                            {onboarding.outlets > 1 && (
                                <p className={cn(TYPE.tc, "m-0 mt-3 text-gray-500")}>
                                    Do this once per outlet. You have {onboarding.outlets}.
                                </p>
                            )}
                        </>
                    )}

                    {key === "website" && (
                        <>
                            <p className={cn(TYPE.tc, "m-0 mb-2 text-gray-500")}>What your site runs on</p>
                            <Seg<SiteBuilder>
                                ariaLabel="What your site runs on"
                                className="max-sm:[&_button]:h-9"
                                value={builder}
                                onChange={(next) => {
                                    setBuilder(next);
                                    setFailure(null);
                                    setSaid(null);
                                }}
                                items={SITE_BUILDER_ORDER.map((k) => ({ key: k, label: SITE_GUIDES[k].short }))}
                            />
                            <GuideSteps steps={guide.steps} className="mt-3" />
                            <AccessAddress className="mt-3" />
                        </>
                    )}

                    {(key === "gsc" || key === "ga4") && <GuideSteps steps={GOOGLE_STEPS} />}
                </DrawerBlock>
            )}

            {item.status === "claimed" && (
                <DrawerBlock label="What happens now">
                    {key === "website" && told?.kind === "user"
                        ? "We check for our user and confirm it here."
                        : "We look for your invite and confirm it here."}
                </DrawerBlock>
            )}

            <DrawerBlock label={held ? "To remove us" : "You stay in control"}>{ACCESS_REMOVE[key]}</DrawerBlock>
        </Drawer>
    );
}
